const MESSAGE_SOURCE = 'whacenter-passkey-extension';
const ALLOWED_PASSKEY_RP_ID = 'web.whatsapp.com';

function base64urlToBuffer(base64url) {
    const padding = '='.repeat((4 - (base64url.length % 4)) % 4);
    const base64 = (base64url + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const buffer = new ArrayBuffer(raw.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < raw.length; i++) {
        view[i] = raw.charCodeAt(i);
    }
    return buffer;
}

function bufferToBase64url(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function assertValidPasskeyOptions(passkeyOptions) {
    if (!passkeyOptions || typeof passkeyOptions !== 'object') {
        throw new Error('Passkey request tidak valid');
    }
    if (passkeyOptions.rpId !== ALLOWED_PASSKEY_RP_ID) {
        throw new Error('Passkey request ditolak: domain tidak valid');
    }
    if (!passkeyOptions.challenge) {
        throw new Error('Passkey request tidak valid: challenge kosong');
    }
}

export function buildPasskeyRequestOptions(passkeyOptions) {
    assertValidPasskeyOptions(passkeyOptions);
    const options = {
        challenge: base64urlToBuffer(passkeyOptions.challenge),
        timeout: passkeyOptions.timeout || 60000,
        rpId: passkeyOptions.rpId,
        userVerification: passkeyOptions.userVerification || 'required',
    };

    if (passkeyOptions.allowCredentials && passkeyOptions.allowCredentials.length > 0) {
        options.allowCredentials = passkeyOptions.allowCredentials.map((cred) => ({
            id: base64urlToBuffer(cred.id),
            type: cred.type || 'public-key',
            transports: cred.transports,
        }));
    }

    return options;
}

export function needsPasskeyExtension() {
    return window.location.hostname !== ALLOWED_PASSKEY_RP_ID;
}

function createRequestId() {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function checkPasskeyExtension(timeoutMs = 800) {
    if (!needsPasskeyExtension()) {
        return Promise.resolve(false);
    }

    return new Promise((resolve) => {
        const requestId = createRequestId();
        let settled = false;

        function handler(event) {
            if (event.source !== window) return;
            if (!event.data || event.data.source !== MESSAGE_SOURCE) return;
            if (event.data.type !== 'PONG' || event.data.requestId !== requestId) return;

            settled = true;
            clearTimeout(timer);
            window.removeEventListener('message', handler);
            resolve(true);
        }

        const timer = setTimeout(() => {
            if (settled) return;
            window.removeEventListener('message', handler);
            resolve(false);
        }, timeoutMs);

        window.addEventListener('message', handler);
        window.postMessage({ source: MESSAGE_SOURCE, type: 'PING', requestId }, '*');
    });
}

function getPasskeyCredentialViaExtension(passkeyOptions, timeoutMs = 120000) {
    assertValidPasskeyOptions(passkeyOptions);

    return new Promise((resolve, reject) => {
        const requestId = createRequestId();

        function handler(event) {
            if (event.source !== window) return;
            if (!event.data || event.data.source !== MESSAGE_SOURCE) return;
            if (event.data.requestId !== requestId) return;

            if (event.data.type === 'PASSKEY_RESULT') {
                clearTimeout(timer);
                window.removeEventListener('message', handler);
                resolve(event.data.passkeyResponse);
            } else if (event.data.type === 'PASSKEY_ERROR') {
                clearTimeout(timer);
                window.removeEventListener('message', handler);
                reject(new Error(event.data.error || 'Passkey gagal'));
            }
        }

        const timer = setTimeout(() => {
            window.removeEventListener('message', handler);
            reject(new Error('Timeout menunggu extension passkey'));
        }, timeoutMs);

        window.addEventListener('message', handler);
        window.postMessage({
            source: MESSAGE_SOURCE,
            type: 'GET_PASSKEY',
            requestId,
            passkeyOptions,
        }, '*');
    });
}

async function getPasskeyCredentialDirect(passkeyOptions) {
    if (!window.PublicKeyCredential) {
        throw new Error('Browser tidak mendukung passkey/WebAuthn');
    }

    const publicKey = buildPasskeyRequestOptions(passkeyOptions);
    const credential = await navigator.credentials.get({ publicKey });

    if (!credential) {
        throw new Error('Passkey dibatalkan');
    }

    return {
        id: credential.id,
        rawId: bufferToBase64url(credential.rawId),
        type: credential.type,
        response: {
            clientDataJSON: bufferToBase64url(credential.response.clientDataJSON),
            authenticatorData: bufferToBase64url(credential.response.authenticatorData),
            signature: bufferToBase64url(credential.response.signature),
            userHandle: credential.response.userHandle
                ? bufferToBase64url(credential.response.userHandle)
                : null,
        },
    };
}

export async function getPasskeyCredential(passkeyOptions) {
    if (needsPasskeyExtension()) {
        const extensionReady = await checkPasskeyExtension();
        if (!extensionReady) {
            throw new Error('Extension WHACENTER Passkey Resolver belum terpasang');
        }
        return getPasskeyCredentialViaExtension(passkeyOptions);
    }
    return getPasskeyCredentialDirect(passkeyOptions);
}

export function isPasskeySupported() {
    return !!(window.PublicKeyCredential && navigator.credentials);
}

export async function isPasskeyResolvable() {
    if (!isPasskeySupported()) {
        return false;
    }
    if (needsPasskeyExtension()) {
        return checkPasskeyExtension();
    }
    return true;
}
